import {
  Card,
  CardContent,
  CardHeader,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Building,
  Globe,
  Mail,
  Hash,
  XCircle,
  FileText,
  ExternalLink,
} from "lucide-react";
import { useCurrentOrganization } from "@/hooks/api";
import { useApiToken } from "@/components/providers/token-provider";
import { isAdminUser } from "@/lib/auth";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UsersTab } from "./organization/UsersTab";

export default function OrganizationPage() {
  const { organization: basicOrg, isTokenLoaded, user } = useApiToken();
  const { data: orgDetails, loading, error } = useCurrentOrganization();
  const isAdmin = isAdminUser(user);

  // Use fetched details or fall back to basic org info from token provider
  const org =
    orgDetails ||
    (basicOrg
      ? {
          id: basicOrg.id,
          name: basicOrg.name,
          slug: basicOrg.slug,
          keycloak_realm: null,
          description: null,
          website: null,
          contact_email: null,
          logo_url: null,
          is_active: true,
        }
      : null);

  if (!isTokenLoaded || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted/30">
        <div className="container mx-auto px-4 py-12 max-w-7xl">
          <div className="mb-10 px-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
              <Skeleton className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl shrink-0" />
              <div className="flex-1 min-w-0">
                <Skeleton className="h-8 w-48 mb-2" />
                <Skeleton className="h-4 w-64" />
              </div>
            </div>
          </div>
          <div className="pl-6 mb-8">
            <Skeleton className="h-10 w-[400px] max-w-full rounded-md" />
          </div>
          <Card className="mb-6 shadow-lg">
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-8">
                <div>
                  <Skeleton className="h-4 w-32 mb-2" />
                  <Skeleton className="h-7 w-48" />
                </div>
                <div>
                  <Skeleton className="h-4 w-28 mb-2" />
                  <Skeleton className="h-9 w-56 rounded-md" />
                </div>
              </div>
              <div>
                <Skeleton className="h-4 w-24 mb-2" />
                <Skeleton className="h-9 w-full rounded-md" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (error || (!loading && !org)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted/30">
        <div className="container mx-auto px-4 py-12 max-w-4xl">
          <Card className="shadow-lg">
            <CardContent className="py-12 text-center">
              <XCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">
                Unable to load organization
              </h2>
              <p className="text-muted-foreground">
                {error?.message || "Organization details not available"}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // TypeScript guard - org is guaranteed to exist here
  if (!org) return null;

  const orgInitial = org.name?.trim()?.charAt(0)?.toUpperCase() || "?";

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted/30">
      <div className="container mx-auto px-4 py-12 max-w-7xl">
        <div className="mb-10 px-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
              <div className="shrink-0">
                {org.logo_url ? (
                  <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl overflow-hidden border-2 border-border bg-background shadow-md ring-2 ring-background/50">
                    <img
                      src={org.logo_url}
                      alt={`${org.name} logo`}
                      className="w-full h-full object-contain p-1"
                    />
                  </div>
                ) : (
                  <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl flex items-center justify-center bg-primary/10 border-2 border-primary/20 shadow-md">
                    <span className="text-3xl sm:text-4xl font-bold text-primary leading-none">
                      {orgInitial}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl sm:text-3xl font-bold text-foreground truncate">
                  {org.name}
                </h1>
                <p className="text-muted-foreground mt-1">
                  Manage settings for your organization
                </p>
              </div>
            </div>
        </div>

        <Tabs defaultValue="overview" className="w-full pl-6">
          <TabsList
            className={`grid w-full mb-8 max-w-[400px] ${isAdmin ? "grid-cols-2" : "grid-cols-1"}`}
          >
            <TabsTrigger value="overview">Overview</TabsTrigger>
            {isAdmin && <TabsTrigger value="users">Users</TabsTrigger>}
          </TabsList>

          <TabsContent value="overview">
            <Card className="mb-6 shadow-lg">
              <CardContent className="space-y-4">
                {/* Name & Slug */}
                <div className="grid sm:grid-cols-2 gap-8">
                  <div>
                    <label className="text-md font-medium text-muted-foreground mb-1.5 flex items-center gap-2">
                      <Building className="h-4 w-4" />
                      Organization Name
                    </label>
                    <div className="text-base text-xl font-semibold text-foreground">
                      {org.name}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1.5 flex items-center gap-2">
                      <Hash className="h-4 w-4" />
                      Slug / Identifier
                    </label>
                    <div className="text-sm font-mono text-muted-foreground px-3 py-2 rounded-md">
                      {org.slug}
                    </div>
                  </div>
                </div>

                {/* Description */}
                {org.description && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1.5 flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Description
                    </label>
                    <div className="text-sm bg-muted/20 px-3 py-2 rounded-md">
                      {org.description}
                    </div>
                  </div>
                )}

                {/* Contact Info */}
                <div className="grid sm:grid-cols-2 gap-6">
                  {org.website && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground mb-1.5 flex items-center gap-2">
                        <Globe className="h-4 w-4" />
                        Website
                      </label>
                      <a
                        href={org.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline flex items-center gap-1"
                      >
                        {org.website}
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </div>
                  )}
                  {org.contact_email && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground mb-1.5 flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        Contact Email
                      </label>
                      <a
                        href={`mailto:${org.contact_email}`}
                        className="text-sm text-primary hover:underline"
                      >
                        {org.contact_email}
                      </a>
                    </div>
                  )}
                </div>

                {/* Organization ID */}
                <div>
                  <label className="text-sm font-medium text-muted-foreground mb-1.5 flex items-center gap-2">
                    <Hash className="h-4 w-4" />
                    Organization ID
                  </label>
                  <div className="text-sm font-mono text-muted-foreground bg-muted/20 px-3 py-2 rounded-md">
                    {org.id}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {isAdmin && (
            <TabsContent value="users">
              <UsersTab />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
}
